import classNames from 'classnames';
import React from 'react';
import { useGetCurrentOwner } from '../../../hooks/apolloHooks/useGetCurrentOwner';
import { CardInvoice } from '../../../integrations/allkort/graphql/interface/Customer';
import { scrollToElement } from '../../../utils/scrollUtils';
import CopyToClipboard from '../../elements/CopyToClipboard';
import FetchingAnimation from '../FetchingAnimation';

type LatestInvoiceProps = {
    wrapperClasses?: string;
    latestInvoice?: CardInvoice | null;
    paidRemainsComplete?: boolean;
}

const LatestInvoice: React.FC<LatestInvoiceProps> = ({
    wrapperClasses = '',
    latestInvoice,
    paidRemainsComplete = false
}) => {

    const { owner, error } = useGetCurrentOwner()
    if (error) console.error('Error fetching owner data:', error.message)

    // Return null if no invoice (new customer)
    if (!latestInvoice) {
        return null;
    }

    /**
     * getStatus
     * 
     * Determine invoice status text and class based on payment and due date
     * 
     * @param returnClassName 
     * @returns 
     */
    const getStatus = (returnClassName = false): string => {
        // Check if invoice is paid (remains is 0)
        if (latestInvoice.remains === 0) {
            return returnClassName ? 'status-paid' : 'Betald';
        }

        // Check if overdue with grace period
        if (latestInvoice.dueDate) {
            const dueDate = new Date(latestInvoice.dueDate);
            dueDate.setHours(0, 0, 0, 0);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Apply grace period (1 day)
            const gracePeriodDeadline = new Date(dueDate);
            gracePeriodDeadline.setDate(gracePeriodDeadline.getDate() + 1);

            // If today is after grace period deadline, it's overdue
            if (today > gracePeriodDeadline) {
                return returnClassName ? 'status-overdue' : 'Förfallen';
            }
        }

        // Otherwise unpaid
        return returnClassName ? 'status-unpaid' : 'Obetald';
    }

    /**
     * handleStatusClick
     * 
     * If invoice is overdue, scroll to its detailed entry on the page
     */
    const handleStatusClick = () => {
        if (getStatus() === 'Förfallen') {
            scrollToElement(`invoice-${latestInvoice.id}`);
        }
    };

    return (
        <div
            className={classNames(
                'inv-state-latest-invoice',
                wrapperClasses
            )}
        >
            <div className="inv-state-latest-invoice__content">
                <h2 className="inv-state-latest-invoice__title">
                    Din senaste faktura
                </h2>

                <div className="inv-state-latest-invoice__details">
                    <div className="ocr-invoice-summary">
                        <label>OCR-nummer</label>
                        <span>
                            {latestInvoice.ocr}
                            <CopyToClipboard text={latestInvoice.ocr} />
                        </span>
                    </div>

                    <div className="due-date-invoice-summary">
                        <label>Förfallodag</label>
                        <span>{latestInvoice.dueDate ? new Date(latestInvoice.dueDate).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}</span>
                    </div>
                    <div className="amount-invoice-summary">
                        <label>{latestInvoice.remains && latestInvoice.remains > 0 ? 'Att betala' : 'Belopp'}</label>
                        <span>
                            {latestInvoice.amount} kr
                            <CopyToClipboard text={latestInvoice.amount.toString()} />
                        </span>
                    </div>

                    <div className="account-number-invoice-summary">
                        <label>Plusgiro</label>
                        <span>
                            {owner?.publicFinancialDetails?.invoicePlusgiro || '-'}
                            <CopyToClipboard text={owner?.publicFinancialDetails?.invoicePlusgiro || ''} />
                        </span>
                    </div>

                    <div className="status-invoice-summary">
                        <label>Status</label>
                        {!paidRemainsComplete ? (
                            <FetchingAnimation width={72} height={31} borderRadius={99} />
                        ) : (
                            <span
                                className={classNames(getStatus(true), getStatus() === 'Förfallen' && 'clickable')}
                                onClick={handleStatusClick}
                                style={{ cursor: getStatus() === 'Förfallen' ? 'pointer' : 'default' }}
                            >
                                {getStatus()}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default LatestInvoice
